import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getAdminSessionIdleMs,
  touchAdminSessionActivity,
} from "@/features/auth/session/adminSessionActivity";
import {
  SESSION_MAX_IDLE_MS,
  SESSION_WARNING_AT_IDLE_MS,
  SESSION_ACTIVITY_THROTTLE_MS,
  SESSION_TICK_INTERVAL_MS,
} from "@/features/auth/session/adminSessionConfig";
import { getAdminAccessToken } from "@/features/auth/session/adminAccessToken";
import { hasAdminSession } from "@/features/auth/session/adminSessionEmail";
import {
  ACCESS_TOKEN_REFRESH_CHECK_MS,
  registerAuthSessionExpiredHandler,
  refreshAdminSession,
  shouldProactivelyRefreshAccessToken,
} from "@/features/auth/session/adminTokenRefresh";
import { useAdminLogout } from "@/features/auth/hooks/useAdminLogout";
import { endAdminSession } from "@/features/auth/session/endAdminSession";
import { SessionExpiryWarningModal } from "./SessionExpiryWarningModal";

const ACTIVITY_EVENTS = [
  "mousedown",
  "keydown",
  "scroll",
  "touchstart",
  "click",
] as const;

export function AdminSessionManager() {
  const navigate = useNavigate();
  const logout = useAdminLogout();
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [idleMs, setIdleMs] = useState(0);
  const [remainingMs, setRemainingMs] = useState(SESSION_MAX_IDLE_MS);
  const lastTouchRef = useRef(0);
  const expiredRef = useRef(false);
  const warningModalOpenRef = useRef(false);

  const recordActivity = useCallback(() => {
    const now = Date.now();
    if (now - lastTouchRef.current < SESSION_ACTIVITY_THROTTLE_MS) {
      return;
    }
    lastTouchRef.current = now;
    touchAdminSessionActivity(now);
    warningModalOpenRef.current = false;
    setShowWarningModal(false);
  }, []);

  const expireSession = useCallback(() => {
    if (expiredRef.current) {
      return;
    }
    expiredRef.current = true;
    warningModalOpenRef.current = false;
    endAdminSession();
    setShowWarningModal(false);
    navigate("/login", {
      replace: true,
      state: { sessionExpired: true },
    });
  }, [navigate]);

  const refreshAccessTokenIfNeeded = useCallback(async () => {
    if (!hasAdminSession()) {
      return false;
    }

    if (!getAdminAccessToken() || shouldProactivelyRefreshAccessToken()) {
      const refreshed = await refreshAdminSession();
      if (!refreshed) {
        expireSession();
        return false;
      }
    }

    return true;
  }, [expireSession]);

  const handleStaySignedIn = useCallback(() => {
    recordActivity();
    void refreshAccessTokenIfNeeded();
  }, [recordActivity, refreshAccessTokenIfNeeded]);

  const handleSignOut = useCallback(() => {
    if (expiredRef.current) {
      return;
    }
    expiredRef.current = true;
    warningModalOpenRef.current = false;
    setShowWarningModal(false);
    logout();
  }, [logout]);

  const isEventInsideWarningModal = useCallback((event: Event) => {
    const target = event.target;
    if (!(target instanceof Node)) {
      return false;
    }
    const modal = document.querySelector('[data-testid="session-expiry-modal"]');
    return modal?.contains(target) ?? false;
  }, []);

  useEffect(() => {
    return registerAuthSessionExpiredHandler(() => {
      expireSession();
    });
  }, [expireSession]);

  useEffect(() => {
    void refreshAccessTokenIfNeeded();
  }, [refreshAccessTokenIfNeeded]);

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        void refreshAccessTokenIfNeeded();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [refreshAccessTokenIfNeeded]);

  useEffect(() => {
    const refreshIntervalId = window.setInterval(() => {
      void refreshAccessTokenIfNeeded();
    }, ACCESS_TOKEN_REFRESH_CHECK_MS);

    return () => window.clearInterval(refreshIntervalId);
  }, [refreshAccessTokenIfNeeded]);

  useEffect(() => {
    recordActivity();

    const onActivity = (event: Event) => {
      if (warningModalOpenRef.current && isEventInsideWarningModal(event)) {
        return;
      }
      recordActivity();
    };

    for (const eventName of ACTIVITY_EVENTS) {
      window.addEventListener(eventName, onActivity, { passive: true });
    }

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        recordActivity();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    const tick = () => {
      const currentIdleMs = getAdminSessionIdleMs();
      setIdleMs(currentIdleMs);

      if (currentIdleMs >= SESSION_MAX_IDLE_MS) {
        expireSession();
        return;
      }

      if (currentIdleMs >= SESSION_WARNING_AT_IDLE_MS) {
        warningModalOpenRef.current = true;
        setShowWarningModal(true);
        setRemainingMs(SESSION_MAX_IDLE_MS - currentIdleMs);
        return;
      }

      warningModalOpenRef.current = false;
      setShowWarningModal(false);
    };

    tick();
    const intervalId = window.setInterval(tick, SESSION_TICK_INTERVAL_MS);

    return () => {
      window.clearInterval(intervalId);
      for (const eventName of ACTIVITY_EVENTS) {
        window.removeEventListener(eventName, onActivity);
      }
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [expireSession, isEventInsideWarningModal, recordActivity]);

  if (!showWarningModal) {
    return null;
  }

  return (
    <SessionExpiryWarningModal
      idleMs={idleMs}
      remainingMs={remainingMs}
      onStaySignedIn={handleStaySignedIn}
      onSignOut={handleSignOut}
    />
  );
}
